import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LockKeyhole, LogIn, LogOut, ShieldCheck, Trash2, UserRound } from "lucide-react";
import { deleteAccount, loginDev, loginWithGoogle, logout, useAuth } from "../lib/auth";
import { useLang, useT } from "../i18n";

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? "";
/** Form đăng nhập bằng email chỉ để thử trên máy dev (backend cũng phải bật DEV_LOGIN). */
const DEV_LOGIN = import.meta.env.VITE_DEV_LOGIN === "true";

interface GoogleId {
  initialize(cfg: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: "popup" }): void;
  renderButton(el: HTMLElement, opts: Record<string, string | number>): void;
}
declare global {
  interface Window { google?: { accounts: { id: GoogleId } } }
}

let gsiPromise: Promise<void> | null = null;
function loadGsi(): Promise<void> {
  gsiPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { gsiPromise = null; reject(new Error("gsi-load")); };
    document.head.appendChild(s);
  });
  return gsiPromise;
}

function GoogleButton({ onError }: { onError: (e: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const lang = useLang();
  useEffect(() => {
    let alive = true;
    loadGsi().then(() => {
      const id = window.google?.accounts.id;
      if (!alive || !id || !ref.current) return;
      id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        ux_mode: "popup",
        callback: (r) => { loginWithGoogle(r.credential).catch(() => onError("login")); },
      });
      id.renderButton(ref.current, { theme: "outline", size: "large", text: "signin_with", shape: "pill", locale: lang, width: 280 });
    }).catch(() => onError("gsi"));
    return () => { alive = false; };
  }, [lang, onError]);
  return <div ref={ref} className="gsi-btn" />;
}

function DevLogin({ onError }: { onError: (e: string) => void }) {
  const [email, setEmail] = useState("dev@example.com");
  return (
    <form className="row" onSubmit={(e) => { e.preventDefault(); loginDev(email).catch(() => onError("login")); }}>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Dev email" />
      <button type="submit" className="btn sm">Dev login</button>
    </form>
  );
}

/** Thẻ đăng nhập: nút Google (và form dev khi bật). */
export function LoginCard({ reason }: { reason?: string }) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="card login-card">
      <span className="icon-tile"><LockKeyhole size={24} aria-hidden="true" /></span>
      <h2>{t("Đăng nhập để tiếp tục", "Sign in to continue")}</h2>
      <p className="muted">
        {reason ?? t(
          "Ngân hàng đề, thi thử, lab và ôn tập dành cho thành viên. Đăng nhập bằng Google — miễn phí, tiến độ được lưu và đồng bộ giữa các thiết bị.",
          "The question bank, mock exams, labs and review are for members. Sign in with Google — it's free, and your progress syncs across devices.",
        )}
      </p>
      {GOOGLE_CLIENT_ID ? <GoogleButton onError={setError} /> : !DEV_LOGIN && (
        <p className="callout warn small">{t("Chưa cấu hình VITE_GOOGLE_CLIENT_ID.", "VITE_GOOGLE_CLIENT_ID is not configured.")}</p>
      )}
      {DEV_LOGIN && <DevLogin onError={setError} />}
      {error && (
        <p className="callout bad small" role="alert">
          {error === "gsi" ? t("Không tải được Google Sign-In. Kiểm tra kết nối hoặc trình chặn quảng cáo.", "Couldn't load Google Sign-In. Check your connection or ad blocker.") : t("Đăng nhập thất bại, vui lòng thử lại.", "Sign-in failed, please try again.")}
        </p>
      )}
      <p className="tiny muted mb-0">
        <ShieldCheck className="ico" size={14} aria-hidden="true" />{" "}
        {t("Chỉ lưu email, tên và tiến độ học. ", "We only store your email, name and study progress. ")}
        <Link to="/privacy">{t("Chính sách quyền riêng tư", "Privacy policy")}</Link>
      </p>
    </div>
  );
}

/** Chỉ hiện nội dung khi đã đăng nhập; nếu chưa, hiện thẻ đăng nhập tại chỗ (giữ nguyên URL). */
export function RequireAuth({ children, allow = false }: { children: React.ReactNode; allow?: boolean }) {
  const auth = useAuth();
  if (auth.status === "authed" || allow) return <>{children}</>;
  return <LoginCard />;
}

/** Nút tài khoản trên header. */
export function AccountMenu() {
  const t = useT();
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (auth.status !== "authed" || !auth.user) {
    if (auth.status === "loading" || auth.status === "idle") return null;
    return <Link to="/login" className="btn sm primary"><LogIn className="ico" size={16} aria-hidden="true" /> <span className="hide-sm">{t("Đăng nhập", "Sign in")}</span></Link>;
  }
  const u = auth.user;
  const onDelete = () => {
    if (window.confirm(t("Xóa vĩnh viễn tài khoản và toàn bộ tiến độ học? Không thể hoàn tác.", "Permanently delete your account and all progress? This cannot be undone."))) {
      void deleteAccount();
    }
  };
  return (
    <div className="menu-wrap" ref={ref}>
      <button type="button" className="avatar-btn" aria-haspopup="menu" aria-expanded={open} aria-label={t(`Tài khoản: ${u.email}`, `Account: ${u.email}`)} onClick={() => setOpen((o) => !o)}>
        {u.avatar_url ? <img src={u.avatar_url} alt="" referrerPolicy="no-referrer" /> : <UserRound size={20} aria-hidden="true" />}
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-title">{u.name ?? u.email}<div className="tiny muted">{u.email}</div></div>
          <button type="button" role="menuitem" className="menu-item" onClick={() => { setOpen(false); void logout(); }}>
            <LogOut size={18} aria-hidden="true" /> <span className="grow">{t("Đăng xuất", "Sign out")}</span>
          </button>
          <button type="button" role="menuitem" className="menu-item danger" onClick={() => { setOpen(false); onDelete(); }}>
            <Trash2 size={18} aria-hidden="true" /> <span className="grow">{t("Xóa tài khoản", "Delete account")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
